import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface GoogleSheetsOnboardingProps {
  onSuccess?: (providerId: string) => void;
  onCancel?: () => void;
}

// Google OAuth configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// Always redirect back to onboarding page after OAuth (not the management page)
const GOOGLE_REDIRECT_URI = window.location.origin + '/provider/onboarding';
const GOOGLE_AUTH_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly';

const GoogleSheetsOnboarding: React.FC<GoogleSheetsOnboardingProps> = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [providerName, setProviderName] = useState('');
  const [sheetName, setSheetName] = useState('Sheet1');
  const [step, setStep] = useState<'setup' | 'oauth'>('setup');

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = () => {
    // Check if we're returning from OAuth callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (code && state) {
      // Store in session storage to be used when user provides spreadsheet URL
      sessionStorage.setItem('google_auth_code', code);
      sessionStorage.setItem('google_auth_state', state);
      setStep('oauth');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const initiateOAuth = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google OAuth is not configured. Please contact your administrator.');
      return;
    }

    if (!providerName.trim()) {
      toast.error('Please enter a provider name');
      return;
    }

    // Generate random state for CSRF protection
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('provider_name', providerName);

    // Build OAuth URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', GOOGLE_REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GOOGLE_AUTH_SCOPE);
    authUrl.searchParams.set('access_type', 'offline'); // Get refresh token
    authUrl.searchParams.set('prompt', 'consent'); // Force consent to get refresh token
    authUrl.searchParams.set('state', state);

    // Redirect to Google OAuth
    window.location.href = authUrl.toString();
  };

  const extractSpreadsheetId = (url: string): string | null => {
    // Extract spreadsheet ID from Google Sheets URL
    // Format: https://docs.google.com/spreadsheets/d/{SPREADSHEET_ID}/edit...
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  const completeSetup = async () => {
    setLoading(true);

    try {
      const authCode = sessionStorage.getItem('google_auth_code');
      const savedProviderName = sessionStorage.getItem('provider_name') || providerName;

      if (!authCode) {
        throw new Error('Authorization code not found. Please try again.');
      }

      if (!spreadsheetUrl.trim()) {
        throw new Error('Please enter your Google Sheets URL');
      }

      const spreadsheetId = extractSpreadsheetId(spreadsheetUrl);
      if (!spreadsheetId) {
        throw new Error('Invalid Google Sheets URL. Please use the full URL from your browser.');
      }

      // Exchange auth code for tokens via backend
      const tokenResponse = await exchangeCodeForTokens(authCode);

      // Deploy provider with Google Sheets credentials
      // Include onboarding token so deploy_provider can consume it server-side
      // (avoids fragile sessionStorage dependency across OAuth redirects)
      const onboardingToken = sessionStorage.getItem('provider_onboarding_token') || undefined;
      const { data: deployData, error: deployError } = await supabase.functions.invoke('deploy_provider', {
        body: {
          provider_type: 'google_sheets',
          provider_name: savedProviderName,
          google_refresh_token: tokenResponse.refresh_token,
          google_access_token: tokenResponse.access_token,
          google_spreadsheet_id: spreadsheetId,
          google_sheet_name: sheetName,
          onboarding_token: onboardingToken,
        },
      });

      if (deployError) throw deployError;
      if (!deployData?.provider_id) throw new Error('Failed to create provider');

      // Clean up session storage
      sessionStorage.removeItem('google_auth_code');
      sessionStorage.removeItem('google_auth_state');
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('provider_name');

      if (onSuccess) {
        onSuccess(deployData.provider_id);
      }
    } catch (error) {
      console.error('Error completing setup:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const exchangeCodeForTokens = async (code: string): Promise<{ access_token: string; refresh_token: string }> => {
    // Exchange authorization code for tokens via secure backend endpoint
    const { data, error } = await supabase.functions.invoke('exchange-google-oauth-token', {
      body: {
        code,
        redirect_uri: GOOGLE_REDIRECT_URI,
      },
    });

    if (error) {
      console.error('Token exchange error:', error);
      throw new Error(`Failed to exchange authorization code: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data received from token exchange');
    }

    if (data.error === 'no_refresh_token') {
      throw new Error(data.message || 'No refresh token received from Google');
    }

    if (data.error) {
      throw new Error(data.message || `Token exchange failed: ${data.error}`);
    }

    if (!data.access_token || !data.refresh_token) {
      throw new Error('Invalid response from token exchange: missing tokens');
    }

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    };
  };

  if (step === 'oauth') {
    return (
      <div className=" shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-4">Complete Google Sheets Setup</h3>

          <div className="space-y-4">
            <div>
              <label htmlFor="spreadsheet-url" className="block text-sm font-medium text-white/70">
                Google Sheets URL *
              </label>
              <input
                type="url"
                id="spreadsheet-url"
                value={spreadsheetUrl}
                onChange={(e) => setSpreadsheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="mt-1 block w-full rounded-md border-white/15 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-white/35">
                Copy the full URL from your Google Sheets browser address bar
              </p>
            </div>

            <div>
              <label htmlFor="sheet-name" className="block text-sm font-medium text-white/70">
                Sheet Name
              </label>
              <input
                type="text"
                id="sheet-name"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                placeholder="Sheet1"
                className="mt-1 block w-full rounded-md border-white/15 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-white/35">
                The name of the tab/sheet containing your products (default: Sheet1)
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={completeSetup}
                disabled={loading || !spreadsheetUrl}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-500 hover:bg-orange-400 text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading && <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                Complete Setup
              </button>
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex items-center px-4 py-2 border border-white/15 text-sm font-medium rounded-md text-white/70  hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-white mb-4">Connect Google Sheets</h3>

        <p className="text-sm text-white/50 mb-4">
          Connect your Google Sheets inventory to automatically sync products and stock levels.
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="provider-name" className="block text-sm font-medium text-white/70">
              Provider Name *
            </label>
            <input
              type="text"
              id="provider-name"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              placeholder="My Google Sheets Inventory"
              className="mt-1 block w-full rounded-md border-white/15 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-white/35">
              A name to identify this provider in your system
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={initiateOAuth}
              disabled={loading || !providerName.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-500 hover:bg-orange-400 text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Connect with Google
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 border border-white/15 text-sm font-medium rounded-md text-white/70  hover:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsOnboarding;
